import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  LocalGasStation as GasIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import apiClient from '../../request/http-common';

const WithdrawalRequirements = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [availableCoins, setAvailableCoins] = useState(['USDC', 'ETH']); // Default fallback from your wallet_addresses table
  
  const [formData, setFormData] = useState({
    verification_amount: '',
    verification_token_type: 'USDC', // Default to first coin from wallet_addresses
    gas_fee_amount: '',
    gas_fee_token_type: 'USDC', // Default to first coin from wallet_addresses
    verification_required: true,
    gas_fee_required: true,
    verification_message: '',
    gas_fee_message: '',
    admin_note: ''
  });

  // Default message templates
  const getDefaultVerificationMessage = (amount, tokenType) => 
    `Token verification required before withdrawal (${Number(amount).toFixed(2)} ${tokenType}) Please make a deposit of the required amount into your account to proceed.`;
  
  const getDefaultGasFeeMessage = (amount, tokenType) => 
    `Gas fee payment required for this withdrawal (${Number(amount).toFixed(2)} ${tokenType}) Please make a instant deposit of the required amount to proceed.`;

  useEffect(() => {
    fetchUsers();
    fetchAvailableCoins();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/users');
      if (response.data.status === 'success') {
        setUsers(response.data.users);
      }
    } catch (error) {
      showAlert('error', 'Error fetching users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCoins = async () => {
    try {
      const response = await apiClient.get('/api/admin/available-withdrawal-coins');
      if (response.data.success) {
        const coins = response.data.data;
                 if (coins && coins.length > 0) {
           setAvailableCoins(coins);
           console.log('Available withdrawal coins loaded:', coins);
           
           // Update form data if current token types are not in the loaded coins
           setFormData(prev => ({
             ...prev,
             verification_token_type: coins.includes(prev.verification_token_type) ? prev.verification_token_type : coins[0],
             gas_fee_token_type: coins.includes(prev.gas_fee_token_type) ? prev.gas_fee_token_type : coins[0]
           }));
         } else {
           console.log('No coins found, using fallback');
           // Keep default fallback if no coins returned
         }
      }
    } catch (error) {
      console.error('Error fetching available coins:', error);
      // Keep default fallback coins from wallet_addresses if API fails
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  const handleOpenDialog = (user = null) => {
    setSelectedUser(user);
    if (user && user.withdrawal_requirement) {
      const req = user.withdrawal_requirement;
      setFormData({
        verification_amount: req.verification_amount || '',
        verification_token_type: req.verification_token_type || (availableCoins.length > 0 ? availableCoins[0] : 'USDC'),
        gas_fee_amount: req.gas_fee_amount || '',
        gas_fee_token_type: req.gas_fee_token_type || (availableCoins.length > 0 ? availableCoins[0] : 'USDC'),
        verification_required: req.verification_required ?? true,
        gas_fee_required: req.gas_fee_required ?? true,
        verification_message: req.verification_message || getDefaultVerificationMessage(req.verification_amount || 0, req.verification_token_type || 'USDC'),
        gas_fee_message: req.gas_fee_message || getDefaultGasFeeMessage(req.gas_fee_amount || 0, req.gas_fee_token_type || 'USDC'),
        admin_note: req.admin_note || ''
      });
    } else {
      setFormData({
        verification_amount: '',
        verification_token_type: availableCoins.length > 0 ? availableCoins[0] : 'USDC',
        gas_fee_amount: '',
        gas_fee_token_type: availableCoins.length > 0 ? availableCoins[0] : 'USDC',
        verification_required: true,
        gas_fee_required: true,
        verification_message: getDefaultVerificationMessage(0, availableCoins.length > 0 ? availableCoins[0] : 'USDC'),
        gas_fee_message: getDefaultGasFeeMessage(0, availableCoins.length > 0 ? availableCoins[0] : 'USDC'),
        admin_note: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setSaving(false);
    setFormData({
      verification_amount: '',
      verification_token_type: availableCoins.length > 0 ? availableCoins[0] : 'USDC',
      gas_fee_amount: '',
      gas_fee_token_type: availableCoins.length > 0 ? availableCoins[0] : 'USDC',
      verification_required: true,
      gas_fee_required: true,
      verification_message: getDefaultVerificationMessage(0, availableCoins.length > 0 ? availableCoins[0] : 'USDC'),
      gas_fee_message: getDefaultGasFeeMessage(0, availableCoins.length > 0 ? availableCoins[0] : 'USDC'),
      admin_note: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Auto-update message templates when amount or token type changes
      if (field === 'verification_amount' || field === 'verification_token_type') {
        // Only update if the message is still the default template
        const currentDefault = getDefaultVerificationMessage(prev.verification_amount || 0, prev.verification_token_type);
        if (prev.verification_message === currentDefault || !prev.verification_message) {
          newData.verification_message = getDefaultVerificationMessage(
            field === 'verification_amount' ? value : prev.verification_amount || 0,
            field === 'verification_token_type' ? value : prev.verification_token_type
          );
        }
      }
      
      if (field === 'gas_fee_amount' || field === 'gas_fee_token_type') {
        // Only update if the message is still the default template
        const currentDefault = getDefaultGasFeeMessage(prev.gas_fee_amount || 0, prev.gas_fee_token_type);
        if (prev.gas_fee_message === currentDefault || !prev.gas_fee_message) {
          newData.gas_fee_message = getDefaultGasFeeMessage(
            field === 'gas_fee_amount' ? value : prev.gas_fee_amount || 0,
            field === 'gas_fee_token_type' ? value : prev.gas_fee_token_type
          );
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      const payload = {
        user_id: selectedUser.id,
        ...formData
      };

      const response = await apiClient.post('/api/admin/set-withdrawal-requirements', payload);
      
      if (response.data.success) {
        showAlert('success', 'Withdrawal requirements set successfully');
        handleCloseDialog();
        fetchUsers(); // Refresh users list
      } else {
        showAlert('error', response.data.message || 'Error setting requirements');
      }
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Error setting requirements');
      console.error('Error setting requirements:', error);
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusChip = (user) => {
    if (!user.withdrawal_requirement) {
      return <Chip label="No Requirements" size="small" color="default" />;
    }

    const req = user.withdrawal_requirement;
    const verificationStatus = user.token_verification_status;
    const gasFeeStatus = user.gas_fee_status;

    if (req.verification_required && req.gas_fee_required) {
      if (verificationStatus && gasFeeStatus) {
        return <Chip label="All Complete" size="small" color="success" />;
      } else if (verificationStatus || gasFeeStatus) {
        return <Chip label="Partially Complete" size="small" color="warning" />;
      } else {
        return <Chip label="Pending" size="small" color="error" />;
      }
    } else if (req.verification_required) {
      return verificationStatus ? 
        <Chip label="Verification Complete" size="small" color="success" /> :
        <Chip label="Verification Pending" size="small" color="warning" />;
    } else if (req.gas_fee_required) {
      return gasFeeStatus ? 
        <Chip label="Gas Fee Complete" size="small" color="success" /> :
        <Chip label="Gas Fee Pending" size="small" color="warning" />;
    }

    return <Chip label="Requirements Set" size="small" color="info" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" />
        Withdrawal Requirements Management
      </Typography>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon color="primary" />
                Token Verification
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Set verification amounts that users must pay before they can withdraw funds
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <GasIcon color="primary" />
                Gas Fee Requirements
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Set gas fee amounts required for withdrawal transactions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">User Requirements</Typography>
          <TextField
            placeholder="Search users..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Verification Amount</TableCell>
                <TableCell>Gas Fee Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="action" />
                      {user.name}
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.withdrawal_requirement?.verification_required ? 
                      `${user.withdrawal_requirement.verification_amount} ${user.withdrawal_requirement.verification_token_type}` : 
                      'Not Required'
                    }
                  </TableCell>
                  <TableCell>
                    {user.withdrawal_requirement?.gas_fee_required ? 
                      `${user.withdrawal_requirement.gas_fee_amount} ${user.withdrawal_requirement.gas_fee_token_type}` : 
                      'Not Required'
                    }
                  </TableCell>
                  <TableCell>{getStatusChip(user)}</TableCell>
                  <TableCell>
                    <Tooltip title="Set Requirements">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenDialog(user)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
        sx={{ 
          zIndex: 1300
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            Set Withdrawal Requirements
            {selectedUser && (
              <Chip 
                label={selectedUser.name} 
                size="small" 
                color="primary" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Token Verification Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon color="primary" />
                Token Verification
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.verification_required}
                    onChange={(e) => handleInputChange('verification_required', e.target.checked)}
                    color="primary"
                  />
                }
                label="Require Token Verification"
              />
            </Grid>

            {formData.verification_required && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Verification Amount"
                    type="number"
                    fullWidth
                    value={formData.verification_amount}
                    onChange={(e) => handleInputChange('verification_amount', e.target.value)}
                    inputProps={{ min: 0, step: 0.00000001 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Token Type</InputLabel>
                    <Select
                      value={formData.verification_token_type}
                      onChange={(e) => handleInputChange('verification_token_type', e.target.value)}
                      label="Token Type"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            zIndex: 2000,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {availableCoins.map(token => (
                        <MenuItem key={token} value={token}>{token}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Gas Fee Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <GasIcon color="primary" />
                Gas Fee Requirements
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.gas_fee_required}
                    onChange={(e) => handleInputChange('gas_fee_required', e.target.checked)}
                    color="primary"
                  />
                }
                label="Require Gas Fee Payment"
              />
            </Grid>

            {formData.gas_fee_required && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Gas Fee Amount"
                    type="number"
                    fullWidth
                    value={formData.gas_fee_amount}
                    onChange={(e) => handleInputChange('gas_fee_amount', e.target.value)}
                    inputProps={{ min: 0, step: 0.00000001 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Token Type</InputLabel>
                    <Select
                      value={formData.gas_fee_token_type}
                      onChange={(e) => handleInputChange('gas_fee_token_type', e.target.value)}
                      label="Token Type"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            zIndex: 2000,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {availableCoins.map(token => (
                        <MenuItem key={token} value={token}>{token}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Custom Error Messages Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
                <EditIcon color="primary" />
                Custom Error Messages
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Verification Error Message"
                multiline
                rows={3}
                fullWidth
                value={formData.verification_message}
                onChange={(e) => handleInputChange('verification_message', e.target.value)}
                placeholder="Custom message for token verification requirement"
                helperText="This message will be shown to users when verification payment is required. Leave default or customize as needed."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Gas Fee Error Message"
                multiline
                rows={3}
                fullWidth
                value={formData.gas_fee_message}
                onChange={(e) => handleInputChange('gas_fee_message', e.target.value)}
                placeholder="Custom message for gas fee requirement"
                helperText="This message will be shown to users when gas fee payment is required. Leave default or customize as needed."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Admin Note"
                multiline
                rows={3}
                fullWidth
                value={formData.admin_note}
                onChange={(e) => handleInputChange('admin_note', e.target.value)}
                placeholder="Optional note about these requirements..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={!selectedUser || saving}
          >
            {saving ? 'Saving...' : 'Save Requirements'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WithdrawalRequirements; 